// Les ENTRETIENS — tous ceux du projet, et la fiche de chacun.
//
// Un entretien appartient à une étude. On le retrouve donc à deux endroits :
// dans la fiche de son étude, et ici, dans la vue qui les rassemble TOUS —
// parce qu'on cherche souvent un entretien par qui ou par quand, pas par étude.
//
// La fiche porte l'enregistrement (audio ou vidéo, lu en flux par l'API, jamais
// par une URL publique) et sa transcription par l'IA, qui verse les verbatims
// dans l'étude comme un import de texte.
import { useRef, useState, type FormEvent } from 'react';
import {
  useCreateInterview,
  useDeleteInterview,
  useInterview,
  useInterviews,
  usePersonas,
  useStudies,
  useTranscribeInterview,
  useUpdateInterview,
  useUploadRecording,
} from '../api/queries';
import type { Interview, ParticipantRole } from '../api/types';
import { TabBar, TabPanel, useTabs } from '../components/Tabs';
import { InterviewAppointment, InterviewImport, InterviewOutputs } from '../interviews/InterviewImport';
import { personName, usePeople } from '../people/api';
import { ApiRequestError } from '../api/client';
import { Button, buttonClass, Chip, EmptyState, ErrorBanner, Eyebrow, Field, Note, Select, Spinner } from '../components/ui';
import { useCanWrite } from '../write/useCanWrite';
import { Drawer } from '../write/Drawer';
import { EditAction, FormShell, TextArea, WriteAction } from '../write/Forms';
import { Link, useRouter } from '../router';
import { personaLine } from '../personas/labels';
import { sourcePath } from '../sources/StudySources';
import { VoiceRecorder } from '../capture/VoiceRecorder';
import { useIsMobile } from '../components/device';
import './project.css';

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiRequestError) return cause.message;
  if (cause instanceof Error) return cause.message;
  return 'Une erreur inattendue est survenue.';
}

const listPath = (projectId: string) => `/projet/${encodeURIComponent(projectId)}/entretiens`;
const detailPath = (projectId: string, id: string) => `${listPath(projectId)}/${encodeURIComponent(id)}`;

function formatDate(d: string | null): string {
  if (!d) return '—';
  const date = new Date(`${d.slice(0, 10)}T12:00:00`);
  return Number.isNaN(date.getTime()) ? d : date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatSize(bytes: number | string | null): string {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1).replace('.', ',')} Go`;
  if (n >= 1024 ** 2) return `${Math.round(n / 1024 ** 2)} Mo`;
  return `${Math.max(1, Math.round(n / 1024))} Ko`;
}

const isVideo = (i: Interview) => (i.recording_type ?? '').startsWith('video/');

/** Séance importée de Lookback : la vidéo y est restée. */
const fromLookback = (i: Interview) => (i.external_ref ?? '').startsWith('lookback:');

/** Où en est l'enregistrement, et sa transcription — une pastille chacun. */
function RecordingChip({ interview }: { interview: Interview }) {
  if (fromLookback(interview)) return <Chip state="succes">Lookback</Chip>;
  if (interview.recording_status === 'ready') {
    return <Chip state="succes">{isVideo(interview) ? 'Vidéo' : 'Audio'}</Chip>;
  }
  if (interview.recording_status === 'uploading') return <Chip state="avertissement">Envoi inachevé</Chip>;
  return <Chip state="vide">Aucun</Chip>;
}

function TranscriptChip({ interview }: { interview: Interview }) {
  switch (interview.transcript_status) {
    case 'done':
      return (
        <Chip state="succes">
          {interview.verbatim_count} verbatim{interview.verbatim_count > 1 ? 's' : ''}
        </Chip>
      );
    case 'running':
    case 'pending':
      return <Chip state="avertissement">En cours</Chip>;
    case 'error':
      return <Chip state="erreur">Échec</Chip>;
    default:
      return <Chip state="vide">—</Chip>;
  }
}

// ---------------------------------------------------------------------------
// FORMULAIRE
// ---------------------------------------------------------------------------

/** Qui commande la mission : la personne interrogée pour la mesure zéro. */
const ROLE_LABELS: Record<ParticipantRole, string> = {
  dirigeant: 'Dirigeant',
  product_owner: 'Product owner',
  commanditaire: 'Commanditaire',
};

export function InterviewForm({
  projectId,
  studyId,
  existing,
  onDone,
  onCreated,
}: {
  projectId: string;
  /** Étude imposée (depuis la fiche d'une étude) : on ne la redemande pas. */
  studyId?: string;
  existing?: Interview;
  onDone: () => void;
  onCreated?: (id: string) => void;
}) {
  const studies = useStudies(projectId);
  const personas = usePersonas(projectId);
  // L'annuaire propose les noms : choisir quelqu'un reprend le persona qu'il incarne.
  const people = (usePeople(projectId).data?.people ?? []).filter((p) => !p.do_not_contact);
  const [study, setStudy] = useState(existing?.study_id ?? studyId ?? '');
  const [participant, setParticipant] = useState(existing?.participant ?? '');
  const [personaId, setPersonaId] = useState(existing?.persona_id ?? '');
  const [conductedOn, setConductedOn] = useState(
    existing?.conducted_on?.slice(0, 10) ?? new Date().toISOString().slice(0, 10),
  );
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [role, setRole] = useState<ParticipantRole | ''>(existing?.participant_role ?? '');
  const create = useCreateInterview(projectId);
  const update = useUpdateInterview(projectId);
  const chooseStudy = !existing && !studyId;
  // Mesure zéro : on interroge celui qui commande la mission, pas un utilisateur.
  // Son rôle remplace alors le persona.
  const baseline = existing?.study_is_baseline
    ?? (studies.data ?? []).find((s) => s.id === study)?.is_baseline
    ?? false;
  const ready = participant.trim().length > 0 && (existing || study);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!ready) return;
    const patch = {
      participant, personaId: personaId || null, conductedOn: conductedOn || null, notes: notes || null,
      participantRole: baseline ? role || null : null,
    };
    try {
      if (existing) {
        await update.mutateAsync({ id: existing.id, patch });
        onDone();
      } else {
        const res = await create.mutateAsync({ ...patch, studyId: study });
        onDone();
        onCreated?.(res.interview.id);
      }
    } catch {
      /* l'erreur est rendue par `FormShell` */
    }
  }

  return (
    <FormShell
      onSubmit={submit}
      submitting={create.isPending || update.isPending}
      error={create.error ?? update.error}
      submitLabel={existing ? 'Enregistrer' : 'Créer l’entretien'}
      disabled={!ready}
    >
      {chooseStudy ? (
        <label className="zy-field">
          <span className="zy-field__label">Étude *</span>
          <Select value={study} onChange={(e) => setStudy(e.target.value)}>
            <option value="">— Choisir l’étude</option>
            {(studies.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {[s.code, s.objective].filter(Boolean).join(' — ') || s.id}
              </option>
            ))}
          </Select>
        </label>
      ) : null}
      <div className="field-row">
        <Field
          label="Participant *"
          value={participant}
          onChange={(e) => {
            setParticipant(e.target.value);
            const match = people.find((p) => personName(p) === e.target.value);
            if (match?.persona_id && !personaId) setPersonaId(match.persona_id);
          }}
          placeholder="Magasinier, site de Lyon"
          hint="Un libellé, pas forcément un nom : l’enregistrement contient déjà sa voix. Les noms de l’annuaire sont proposés."
          list="itw-people"
        />
        <datalist id="itw-people">
          {people.map((p) => <option key={p.id} value={personName(p)}>{p.job_title ?? undefined}</option>)}
        </datalist>
        <Field label="Date" type="date" value={conductedOn} onChange={(e) => setConductedOn(e.target.value)} />
      </div>
      {baseline ? (
        <label className="zy-field">
          <span className="zy-field__label">Rôle dans la mission</span>
          <Select value={role} onChange={(e) => setRole(e.target.value as ParticipantRole | '')}>
            <option value="">— Non précisé</option>
            {(Object.keys(ROLE_LABELS) as ParticipantRole[]).map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </Select>
        </label>
      ) : null}
      <label className="zy-field">
        <span className="zy-field__label">Persona</span>
        <Select value={personaId} onChange={(e) => setPersonaId(e.target.value)}>
          <option value="">— Aucun</option>
          {(personas.data ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {personaLine(p)}
            </option>
          ))}
        </Select>
      </label>
      <TextArea label="Notes" value={notes} onChange={setNotes} rows={3} placeholder="Contexte, déroulé, ce qui a marqué." />
      {existing ? null : (
        <Note>L’enregistrement audio ou vidéo s’envoie ensuite, sur la fiche de l’entretien.</Note>
      )}
    </FormShell>
  );
}

// ---------------------------------------------------------------------------
// LISTE
// ---------------------------------------------------------------------------

function InterviewTable({ projectId, interviews, showStudy }: { projectId: string; interviews: Interview[]; showStudy: boolean }) {
  return (
    <div className="zy-table-cadre">
      {/* `zy-table--empilable` : sur téléphone, chaque ligne devient une carte, et
          chaque cellule reprend son libellé (`data-label`). */}
      <table className="zy-table zy-table--empilable">
        <thead>
          <tr>
            <th>Date</th>
            <th>Participant</th>
            {showStudy ? <th>Étude</th> : null}
            <th>Enregistrement</th>
            <th>Transcription</th>
          </tr>
        </thead>
        <tbody>
          {interviews.map((i) => (
            <tr key={i.id}>
              <td data-label="Date">{formatDate(i.conducted_on)}</td>
              <td className="principal">
                <Link to={detailPath(projectId, i.id)} className="card-title-link">
                  {i.participant}
                </Link>
                {i.participant_role ? <span className="persona-row-meta"> · {ROLE_LABELS[i.participant_role]}</span> : null}
                {i.persona_name ? <span className="persona-row-meta"> · {i.persona_name}</span> : null}
              </td>
              {showStudy ? (
                <td data-label="Étude">
                  <Link to={`/projet/${encodeURIComponent(projectId)}/etudes/${encodeURIComponent(i.study_id)}`}>
                    {i.study_code ?? i.study_objective ?? i.study_id}
                  </Link>
                </td>
              ) : null}
              <td data-label="Enregistrement">
                <RecordingChip interview={i} />
              </td>
              <td data-label="Transcription">
                <TranscriptChip interview={i} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function InterviewsScreen({ projectId }: { projectId: string }) {
  // TOUS les entretiens du projet, sans filtre : la colonne « Étude » dit d'où
  // vient chacun. Pour ne voir que ceux d'une étude, on ouvre la fiche de l'étude.
  const query = useInterviews(projectId);
  const canWrite = useCanWrite(projectId);
  const { navigate } = useRouter();

  // « Nouvel entretien » ouvre sa page (055) : inviter dans une étude existante,
  // ou construire une étude d'entretiens. Saisir un entretien déjà mené y reste.
  const newInterview = canWrite ? (
    <Button variant="accent" onClick={() => navigate(`/projet/${encodeURIComponent(projectId)}/entretiens/nouveau`)}>
      Nouvel entretien
    </Button>
  ) : null;

  return (
    <main className="screen">
      <div className="zy-page-head__texte">
        <h1 className="t-h1">Entretiens</h1>
        <p className="zy-intro">
          Tous les entretiens du projet, quelle que soit leur étude. Chacun garde son enregistrement
          audio ou vidéo, et sa transcription verse les verbatims dans l’étude.
        </p>
      </div>

      {query.isPending ? (
        <div className="screen-loading">
          <Spinner label="Chargement des entretiens" />
        </div>
      ) : query.isError ? (
        <ErrorBanner>{errorMessage(query.error)}</ErrorBanner>
      ) : query.data.interviews.length === 0 ? (
        <EmptyState
          title="Aucun entretien pour l’instant"
          description="Créez un entretien dans une étude, puis envoyez son enregistrement : il sera transcrit, et ses verbatims rattachés à l’étude."
          {...(newInterview ? { action: newInterview } : {})}
        />
      ) : (
        <>
          <InterviewTable projectId={projectId} interviews={query.data.interviews} showStudy />
          {/* Le bouton vient APRÈS la liste : on lit ce qui existe, puis on ajoute. */}
          {newInterview ? <div className="screen-actions">{newInterview}</div> : null}
        </>
      )}
    </main>
  );
}

/** Le bloc « Entretiens » de la fiche d'une étude. */
export function StudyInterviewsSection({ projectId, studyId }: { projectId: string; studyId: string }) {
  const query = useInterviews(projectId, studyId);
  const canWrite = useCanWrite(projectId);
  const { navigate } = useRouter();
  return (
    <section className="test-block">
      <div className="block-head">
        <h2 className="t-h3">Entretiens</h2>
        {canWrite ? (
          <WriteAction label="Nouvel entretien" title="Nouvel entretien" description="Rattaché à cette étude. L’enregistrement s’envoie ensuite.">
            {(close) => (
              <InterviewForm
                projectId={projectId}
                studyId={studyId}
                onDone={close}
                onCreated={(id) => navigate(detailPath(projectId, id))}
              />
            )}
          </WriteAction>
        ) : null}
      </div>
      {query.isPending ? (
        <Spinner label="Chargement des entretiens" />
      ) : query.isError ? (
        <ErrorBanner>{errorMessage(query.error)}</ErrorBanner>
      ) : query.data.interviews.length === 0 ? (
        <p className="card-note">Aucun entretien rattaché à cette étude.</p>
      ) : (
        <InterviewTable projectId={projectId} interviews={query.data.interviews} showStudy={false} />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// FICHE
// ---------------------------------------------------------------------------

function RecordingUpload({
  projectId,
  interview,
  onUploaded,
  live = false,
}: {
  projectId: string;
  interview: Interview;
  /** Enchaîne sur la transcription : c'est ce qu'on attend d'un envoi. */
  onUploaded: () => void;
  /** Sur le terrain (mode mobile) : enregistrer la voix ou filmer sur place. */
  live?: boolean;
}) {
  const upload = useUploadRecording(projectId);
  const [progress, setProgress] = useState<number | null>(null);
  // L'accord de la personne enregistrée s'ATTESTE à chaque envoi : le serveur
  // refuse l'enregistrement sans cette attestation, qu'il horodate.
  const [consent, setConsent] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const camera = useRef<HTMLInputElement>(null);
  const replacing = interview.recording_status === 'ready';

  async function send(file: File): Promise<boolean> {
    setProgress(0);
    try {
      await upload.mutateAsync({ id: interview.id, file, onProgress: setProgress });
      onUploaded();
      return true;
    } catch {
      /* l'erreur est rendue sous le bouton */
      return false;
    } finally {
      setProgress(null);
      if (input.current) input.current.value = '';
      if (camera.current) camera.current.value = '';
    }
  }

  const consentBox = (
    <label className="terms-consent recording-consent">
      <input className="zy-check" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
      <span>
        La personne interrogée a été informée et a accepté d’être enregistrée ; l’enregistrement sera transcrit
        par une IA hébergée dans l’Union européenne.
      </span>
    </label>
  );
  const progressBar = progress !== null ? (
    <div className="upload-progress">
          <progress className="zy-progress" value={Math.round(progress * 100)} max={100} aria-label="Envoi du fichier" />
          <em>{Math.round(progress * 100)} %</em>
        </div>
  ) : null;

  if (live) {
    // L'accord d'abord : on ne lance pas le micro ni la caméra sans lui.
    return (
      <div className="recording-upload capture-choices">
        {consentBox}
        {replacing ? <p className="zy-field__aide">Un nouvel enregistrement remplacera l’actuel.</p> : null}
        <VoiceRecorder storageKey={`interview:${interview.id}`} disabled={!consent} busy={progress !== null} onSend={send} />
        {/* `capture` : le téléphone ouvre directement la caméra, en vidéo. */}
        <input
          ref={camera}
          type="file"
          accept="video/*"
          capture="environment"
          className="sr-only"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void send(f);
          }}
        />
        <input
          ref={input}
          type="file"
          accept="audio/*,video/*"
          className="sr-only"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void send(f);
          }}
        />
        <div className="capture-or">
          <Button variant="ghost" disabled={!consent || progress !== null} onClick={() => camera.current?.click()}>
            Filmer
          </Button>
          <Button variant="ghost" disabled={!consent || progress !== null} onClick={() => input.current?.click()}>
            Choisir un fichier
          </Button>
        </div>
        {progressBar}
        {upload.error ? <ErrorBanner>{errorMessage(upload.error)}</ErrorBanner> : null}
      </div>
    );
  }

  return (
    <div className="recording-upload">
      <input
        ref={input}
        type="file"
        accept="audio/*,video/*"
        className="sr-only"
        id={`rec-${interview.id}`}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void send(f);
        }}
      />
      {consentBox}
      <Button
        variant={replacing ? 'ghost' : 'accent'}
        loading={progress !== null}
        disabled={!consent}
        onClick={() => input.current?.click()}
      >
        {replacing ? 'Remplacer l’enregistrement' : 'Envoyer l’enregistrement'}
      </Button>
      {progressBar ?? (
        <span className="zy-field__aide">Audio ou vidéo, 2 Go maximum.{replacing ? ' Remplacer supprime le fichier actuel.' : ''}</span>
      )}
      {upload.error ? <ErrorBanner>{errorMessage(upload.error)}</ErrorBanner> : null}
    </div>
  );
}

function DeleteInterviewAction({ projectId, interview, onDeleted }: { projectId: string; interview: Interview; onDeleted: () => void }) {
  const [open, setOpen] = useState(false);
  const del = useDeleteInterview(projectId);
  return (
    <>
      <Button variant="link-danger" size="s" type="button" onClick={() => setOpen(true)}>
        Supprimer
      </Button>
      <Drawer
        open={open}
        title={`Supprimer l’entretien « ${interview.participant} » ?`}
        description="L’enregistrement est supprimé définitivement. Les verbatims déjà extraits restent dans l’étude : ils peuvent étayer des insights."
        onClose={() => setOpen(false)}
      >
        <div className="write-form">
          {del.error ? <ErrorBanner>{errorMessage(del.error)}</ErrorBanner> : null}
          <Button
            variant="danger"
            loading={del.isPending}
            onClick={() => {
              del.mutate(interview.id, { onSuccess: () => { setOpen(false); onDeleted(); } });
            }}
          >
            Supprimer l’entretien et son enregistrement
          </Button>
        </div>
      </Drawer>
    </>
  );
}

// Trois onglets : le rendez-vous, l'import (avec l'enregistrement et la
// transcription qui en sortent), les synthèses.
type InterviewTab = 'rendez-vous' | 'importer' | 'syntheses';
const INTERVIEW_TABS: readonly InterviewTab[] = ['rendez-vous', 'importer', 'syntheses'];

export function InterviewDetailScreen({ projectId, interviewId }: { projectId: string; interviewId: string }) {
  const query = useInterview(interviewId);
  const canWrite = useCanWrite(projectId);
  const transcribe = useTranscribeInterview(projectId);
  const { navigate } = useRouter();
  const backTo = listPath(projectId);
  // Sur le terrain, la fiche s'ouvre là où l'on enregistre.
  const mobile = useIsMobile();
  const [tab, setTab] = useTabs<InterviewTab>('entretien', INTERVIEW_TABS, mobile ? 'importer' : 'rendez-vous');

  if (query.isPending) {
    return (
      <main className="screen">
        <div className="screen-loading">
          <Spinner label="Chargement de l’entretien" />
        </div>
      </main>
    );
  }
  if (query.isError) {
    return (
      <main className="screen">
        <Link to={backTo} className="zy-retour">← Entretiens</Link>
        <ErrorBanner>{errorMessage(query.error)}</ErrorBanner>
        <EmptyState
          title="Entretien introuvable dans votre périmètre"
          description="Il n’existe pas, ou il appartient à un projet auquel vous n’avez pas accès."
        />
      </main>
    );
  }

  // `recordings` : le serveur peut-il recevoir un fichier ? (Non en production
  // tant qu'aucun bucket n'est configuré.) Absent = oui, comme avant.
  const { interview: it, transcriber, recordings = true, outputs = [], importer = { youtube: false, generate: false } } = query.data;
  const src = `/api/interviews/${encodeURIComponent(it.id)}/recording`;
  const running = it.transcript_status === 'running' || transcribe.isPending;
  const canTranscribe = canWrite && it.recording_status === 'ready' && it.transcript_status !== 'done' && !running;

  return (
    <main className="screen">
      <Link to={backTo} className="zy-retour">← Entretiens</Link>

      <div className="zy-page-head__texte">
        <Eyebrow>Entretien · {formatDate(it.conducted_on)}</Eyebrow>
        <h1 className="t-h1">{it.participant}</h1>
        <div className="test-head-chips">
          <RecordingChip interview={it} />
          <TranscriptChip interview={it} />
        </div>
        {canWrite ? (
          <div className="screen-actions">
            <EditAction title="Modifier l’entretien" description="Participant, date, persona, notes.">
              {(close) => <InterviewForm projectId={projectId} existing={it} onDone={close} />}
            </EditAction>
            <DeleteInterviewAction projectId={projectId} interview={it} onDeleted={() => navigate(backTo)} />
          </div>
        ) : null}
      </div>

      <dl className="facts">
        <div>
          <dt>Étude</dt>
          <dd>
            <Link to={`/projet/${encodeURIComponent(projectId)}/etudes/${encodeURIComponent(it.study_id)}`}>
              {it.study_code ?? it.study_objective ?? it.study_id}
            </Link>
          </dd>
        </div>
        {it.persona_name ? (
          <div>
            <dt>Persona</dt>
            <dd>{it.persona_name}</dd>
          </div>
        ) : null}
      </dl>
      {it.notes ? <p className="card-note">{it.notes}</p> : null}

      <TabBar
        label="Sections de l’entretien"
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'rendez-vous', label: 'Rendez-vous' },
          { id: 'importer', label: 'Importer' },
          { id: 'syntheses', label: 'Synthèses', count: outputs.length || null },
        ]}
      />

      {tab === 'rendez-vous' ? (
        <TabPanel id="rendez-vous">
          <InterviewAppointment projectId={projectId} interview={it} canWrite={canWrite} />
        </TabPanel>
      ) : null}

      {tab === 'importer' ? (
        <TabPanel id="importer">
          {canWrite ? (
            <InterviewImport
              projectId={projectId}
              interview={it}
              importer={importer}
              captureSlot={mobile && recordings && !fromLookback(it) ? (
                <RecordingUpload
                  live
                  projectId={projectId}
                  interview={it}
                  onUploaded={() => {
                    if (transcriber) transcribe.mutate(it.id);
                    document.getElementById('itw-transcription')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                />
              ) : undefined}
              recordingSlot={fromLookback(it) ? (
                <p className="card-note">Séance importée de Lookback : la vidéo reste dans Lookback.</p>
              ) : !recordings ? (
                <Note tone="lecture">L’envoi d’enregistrements n’est pas encore ouvert sur ce serveur.</Note>
              ) : (
                <RecordingUpload
                  projectId={projectId}
                  interview={it}
                  onUploaded={() => {
                    if (transcriber) transcribe.mutate(it.id);
                    document.getElementById('itw-transcription')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                />
              )}
            />
          ) : (
            <Note tone="lecture">Lecture seule : l’import est réservé aux responsables du projet et aux administrateurs.</Note>
          )}
          {/* Ce que l'import a donné : l'enregistrement et sa transcription. */}
          <div id="itw-transcription">
      <section className="test-block">
        <h2 className="t-h3">Enregistrement</h2>
        {fromLookback(it) ? (
          // Importée de Lookback : la vidéo y est restée, la transcription est
          // ici. Pas d'envoi de fichier — il remettrait la transcription à zéro.
          <p className="card-note">
            Séance importée de Lookback : la vidéo reste dans Lookback.{' '}
            {it.external_url ? (
              <a href={it.external_url} target="_blank" rel="noopener noreferrer">Revoir la séance dans Lookback</a>
            ) : null}
          </p>
        ) : it.recording_status === 'ready' ? (
          <>
            {/* `key` : un fichier remplacé doit recharger le lecteur. */}
            {isVideo(it) ? (
              <video key={`${it.recording_name}-${it.recording_size}`} className="recording-player" controls preload="metadata" src={src} />
            ) : (
              <audio key={`${it.recording_name}-${it.recording_size}`} className="recording-player" controls preload="metadata" src={src} />
            )}
            <p className="zy-field__aide">
              {[it.recording_name, formatSize(it.recording_size)].filter(Boolean).join(' · ')}
            </p>
          </>
        ) : (
          <p className="card-note">
            {it.recording_status === 'uploading'
              ? 'Un envoi a commencé sans aboutir. Renvoyez le fichier.'
              : 'Aucun enregistrement pour cet entretien.'}
          </p>
        )}
      </section>

      <section className="test-block">
        <div className="block-head">
          <h2 className="t-h3">Transcription</h2>
          {canTranscribe && transcriber ? (
            <Button variant="ghost" onClick={() => transcribe.mutate(it.id)}>
              {it.transcript_status === 'error' ? 'Relancer la transcription' : 'Transcrire'}
            </Button>
          ) : null}
        </div>
        {running ? (
          <p className="card-note" role="status">
            <Spinner label="Transcription en cours" /> Transcription en cours : l’IA écoute l’enregistrement
            et l’écrit tour de parole par tour de parole. Comptez une à quelques minutes selon la durée.
          </p>
        ) : it.transcript_status === 'error' ? (
          <ErrorBanner>{it.transcript_error ?? 'La transcription a échoué.'}</ErrorBanner>
        ) : it.transcript_status === 'done' ? (
          // Transcrit : on découpe soi-même les passages qui feront preuve (migration 047).
          <div className="block-head">
            <p className="zy-intro">
              {it.verbatim_count > 0
                ? `${it.verbatim_count} verbatim${it.verbatim_count > 1 ? 's' : ''} retenu${it.verbatim_count > 1 ? 's' : ''} dans cette transcription, avec leur locuteur et leur timecode.`
                : 'Transcription prête : relisez-la et surlignez les passages qui feront preuve.'}
            </p>
            {it.source_id ? (
              <Link to={sourcePath(projectId, it.study_id, it.source_id)} className={buttonClass(it.verbatim_count > 0 ? 'ghost' : 'primary')}>
                {it.verbatim_count > 0 ? 'Relire et découper' : 'Découper la transcription'}
              </Link>
            ) : null}
          </div>
        ) : !transcriber ? (
          <p className="card-note">La transcription automatique n’est pas configurée sur ce serveur.</p>
        ) : it.recording_status !== 'ready' && recordings ? (
          <p className="card-note">La transcription se lance dès que l’enregistrement est envoyé.</p>
        ) : null}
        {transcribe.error ? <ErrorBanner>{errorMessage(transcribe.error)}</ErrorBanner> : null}
        {it.transcript_text ? <div className="transcript">{it.transcript_text}</div> : null}
      </section>
          </div>
        </TabPanel>
      ) : null}

      {tab === 'syntheses' ? (
        <TabPanel id="syntheses">
          <InterviewOutputs outputs={outputs} />
        </TabPanel>
      ) : null}

    </main>
  );
}
